import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE, authHeaders } from '../constants/api';
import { SEVERITY_OPTIONS } from '../constants/alarmConstants';
import { useAuth } from '../context/AuthContext';
import { SearchDropdown, TooltipIcon } from '../UI-Elements';
import ErrorBoundary from './ErrorBoundary';
import { Bestätigen, Abbrechen } from '../icons';
import './NewAlarmrule.css';

const CONDITION_OPTIONS = [
  { value: 'ABOVE', label: 'einen Grenzwert überschreitet' },
  { value: 'BELOW', label: 'einen Grenzwert unterschreitet' },
  { value: 'OUTSIDE', label: 'außerhalb eines Bereichs liegt' },
  { value: 'INSIDE', label: 'innerhalb eines Bereichs liegt' },
  { value: 'STATE', label: 'einem Zustand entspricht' },
];

const ENTITY_MODE_OPTIONS = [
  { value: 'DEVICE', label: 'Endgerät' },
  { value: 'DEVICE_PROFILE', label: 'Gerätetyp' },
];

function SectionHeader({ name, number, title, tooltip, required }) {
  return (
    <div className="new-alarmrule-field-label">
      <span className="new-alarmrule-field-number" aria-hidden="true">{number}</span>
      <span id={`alarmrule-label-${name}`}>{title}{required && <span className="new-alarmrule-required">*</span>}</span>
      {tooltip && <TooltipIcon text={tooltip} idSuffix={`alarmrule-${name}`} />}
    </div>
  );
}

function FieldError({ children }) {
  return <div className="new-alarmrule-error-text" role="alert">{children}</div>;
}

function RadioGroup({ name, labelledBy, options, value, onChange }) {
  return (
    <div className="new-alarmrule-radio-group" role="radiogroup" aria-labelledby={labelledBy}>
      {options.map(o => (
        <label key={o.value} className="new-alarmrule-radio-label">
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
          />
          {o.label}
        </label>
      ))}
    </div>
  );
}

function NewAlarmrule() {
  const { authToken: token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefillDeviceId = location.state?.prefillDeviceId || '';
  const prefillAppliedRef = useRef(false);

  const [name, setName] = useState('');
  const [severity, setSeverity] = useState('');
  const [severityText, setSeverityText] = useState('');
  const [entityMode, setEntityMode] = useState('DEVICE');
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [entityText, setEntityText] = useState('');
  const [dataKeys, setDataKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [keyText, setKeyText] = useState('');
  const [condition, setCondition] = useState('ABOVE');
  const [threshold, setThreshold] = useState('');
  const [lowerThreshold, setLowerThreshold] = useState('');
  const [upperThreshold, setUpperThreshold] = useState('');
  const [stateValue, setStateValue] = useState('');

  const [submitError, setSubmitError] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [touched, setTouched] = useState({});

  const touch = (field) => setTouched(prev => (prev[field] ? prev : { ...prev, [field]: true }));

  // Load entities when mode changes
  useEffect(() => {
    if (!token) return;
    setEntities([]);
    setSelectedEntity('');
    setEntityText('');
    setDataKeys([]);
    setSelectedKey('');
    setKeyText('');
    setTouched(prev => ({ ...prev, entity: false, key: false }));

    async function loadEntities() {
      try {
        if (entityMode === 'DEVICE') {
          const res = await fetch(
            `${API_BASE}/tenant/devices?pageSize=1000&page=0`,
            { headers: authHeaders(token) }
          );
          if (!res.ok) return;
          const data = await res.json();
          setEntities((data.data || []).map(d => ({ id: d.id.id, name: d.name })));
        } else {
          const res = await fetch(
            `${API_BASE}/deviceProfiles?pageSize=1000&page=0`,
            { headers: authHeaders(token) }
          );
          if (!res.ok) return;
          const data = await res.json();
          setEntities((data.data || []).filter(p => p.name !== 'default').map(p => ({ id: p.id.id, name: p.name })));
        }
      } catch {
        // ignore
      }
    }
    loadEntities();
  }, [entityMode, token]);

  // Pre-fill device selection when navigating from DeviceDetails
  useEffect(() => {
    if (prefillDeviceId && entities.length > 0 && !prefillAppliedRef.current) {
      const match = entities.find(e => e.id === prefillDeviceId);
      if (match) {
        setSelectedEntity(prefillDeviceId);
        setEntityText(match.name);
        prefillAppliedRef.current = true;
      }
    }
  }, [prefillDeviceId, entities]);

  // Load telemetry keys when entity selection changes
  useEffect(() => {
    if (!token || !selectedEntity) {
      setDataKeys([]);
      setSelectedKey('');
      return;
    }

    async function loadKeys() {
      try {
        if (entityMode === 'DEVICE') {
          const res = await fetch(
            `${API_BASE}/plugins/telemetry/DEVICE/${selectedEntity}/keys/timeseries`,
            { headers: authHeaders(token) }
          );
          if (!res.ok) return;
          const keys = await res.json();
          setDataKeys(keys || []);
        } else {
          const devRes = await fetch(
            `${API_BASE}/tenant/devices?pageSize=1&page=0&deviceProfileId=${selectedEntity}`,
            { headers: authHeaders(token) }
          );
          if (!devRes.ok) return;
          const devData = await devRes.json();
          const firstDevice = (devData.data || [])[0];
          if (!firstDevice) { setDataKeys([]); return; }

          const res = await fetch(
            `${API_BASE}/plugins/telemetry/DEVICE/${firstDevice.id.id}/keys/timeseries`,
            { headers: authHeaders(token) }
          );
          if (!res.ok) return;
          const keys = await res.json();
          setDataKeys(keys || []);
        }
      } catch {
        // ignore
      }
    }
    loadKeys();
  }, [selectedEntity, entityMode, token]);

  const isSingleThreshold = condition === 'ABOVE' || condition === 'BELOW';
  const isStateCondition = condition === 'STATE';
  const thresholdOrderValid = isSingleThreshold || isStateCondition ||
    lowerThreshold.trim() === '' || upperThreshold.trim() === '' ||
    Number(lowerThreshold) < Number(upperThreshold);

  const isValid =
    name.trim() !== '' &&
    severity !== '' &&
    selectedEntity !== '' &&
    selectedKey !== '' &&
    (isSingleThreshold
      ? threshold.trim() !== ''
      : isStateCondition
        ? stateValue.trim() !== ''
        : (lowerThreshold.trim() !== '' && upperThreshold.trim() !== '')) &&
    thresholdOrderValid;

  const showError = (field) => showErrors || !!touched[field];
  const nameError = showError('name') && name.trim() === '';
  const severityError = showError('severity') && severity === '';
  const entityError = showError('entity') && selectedEntity === '';
  const keyError = showError('key') && !!selectedEntity && selectedKey === '';
  const stateError = showError('state') && stateValue.trim() === '';
  const thresholdError = showError('threshold') && threshold.trim() === '';
  const lowerError = showError('lower') && lowerThreshold.trim() === '';
  const upperError = showError('upper') && upperThreshold.trim() === '';
  const rangeOrderError = !thresholdOrderValid;

  const selectionErrorText = (text) =>
    text.trim() ? 'Bitte einen Eintrag aus den Vorschlägen auswählen.' : 'Dieses Feld ist erforderlich.';

  const changeCondition = (value) => {
    setCondition(value);
    setThreshold('');
    setLowerThreshold('');
    setUpperThreshold('');
    setStateValue('');
    setTouched(prev => ({ ...prev, state: false, threshold: false, lower: false, upper: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);

    // Build predicates based on condition type
    const predicates = [];

    if (condition === 'ABOVE') {
      predicates.push({
        type: 'NUMERIC',
        operation: 'GREATER_OR_EQUAL',
        ignoreCase: false,
        predicates: null,
        value: { staticValue: Number(threshold) },
      });
    } else if (condition === 'BELOW') {
      predicates.push({
        type: 'NUMERIC',
        operation: 'LESS_OR_EQUAL',
        ignoreCase: false,
        predicates: null,
        value: { staticValue: Number(threshold) },
      });
    } else if (condition === 'OUTSIDE') {
      predicates.push({
        type: 'NUMERIC',
        operation: 'LESS_OR_EQUAL',
        ignoreCase: false,
        predicates: null,
        value: { staticValue: Number(lowerThreshold) },
      });
      predicates.push({
        type: 'NUMERIC',
        operation: 'GREATER_OR_EQUAL',
        ignoreCase: false,
        predicates: null,
        value: { staticValue: Number(upperThreshold) },
      });
    } else if (condition === 'STATE') {
      const trimmed = stateValue.trim();
      const isNumericState = trimmed !== '' && !isNaN(Number(trimmed));
      predicates.push({
        type: isNumericState ? 'NUMERIC' : 'STRING',
        operation: 'EQUAL',
        ignoreCase: !isNumericState,
        predicates: null,
        value: { staticValue: isNumericState ? Number(trimmed) : trimmed },
      });
    } else {
      // INSIDE
      predicates.push({
        type: 'NUMERIC',
        operation: 'GREATER_OR_EQUAL',
        ignoreCase: false,
        predicates: null,
        value: { staticValue: Number(lowerThreshold) },
      });
      predicates.push({
        type: 'NUMERIC',
        operation: 'LESS_OR_EQUAL',
        ignoreCase: false,
        predicates: null,
        value: { staticValue: Number(upperThreshold) },
      });
    }

    const body = {
      entityId: {
        entityType: entityMode,
        id: selectedEntity,
      },
      name: name.trim(),
      type: 'ALARM',
      debugSettings: {
        failuresEnabled: true,
        allEnabled: true,
      },
      configuration: {
        type: 'ALARM',
        arguments: {
          [selectedKey]: {
            refEntityKey: {
              type: 'TS_LATEST',
              key: selectedKey,
            },
            defaultValue: '',
          },
        },
        propagate: false,
        propagateToOwner: false,
        propagateToOwnerHierarchy: false,
        propagateToTenant: false,
        propagateRelationTypes: null,
        createRules: {
          [severity]: {
            condition: {
              expression: {
                type: 'SIMPLE',
                operation: 'AND',
                filters: [
                  {
                    argument: selectedKey,
                    valueType: (() => {
                      if (condition !== 'STATE') return 'NUMERIC';
                      const trimmed = stateValue.trim();
                      return (trimmed !== '' && !isNaN(Number(trimmed))) ? 'NUMERIC' : 'STRING';
                    })(),
                    predicates,
                    operation: condition === 'INSIDE' ? 'AND' : 'OR',
                  },
                ],
              },
              type: 'SIMPLE',
            },
            alarmDetails: null,
            dashboardId: null,
          },
        },
        clearRule: null,
      },
    };

    try {
      setSubmitError('');
      const res = await fetch(`${API_BASE}/calculatedField`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarmregel konnte nicht erstellt werden (HTTP ${res.status})`);
      }
      navigate('/alarm-rules', { state: { statusMessage: `Alarmregel „${name.trim()}" wurde erfolgreich erstellt.` } });
    } catch (err) {
      setSubmitError(`Alarmregel konnte nicht erstellt werden: ${err.message || 'Netzwerkfehler'}.`);
    }
  };

  const entityLabel = entityMode === 'DEVICE' ? 'Endgerät' : 'Gerätetyp';
  const valueSectionTitle = isStateCondition ? 'Zustand' : isSingleThreshold ? 'Grenzwert' : 'Grenzwerte';

  return (
    <div className="new-alarmrule">
      <div className="device-toolbar">
        <div className="device-toolbar-left">
          <h1 className="device-title">Neue Alarmregel erstellen</h1>
        </div>
      </div>

      <form className="new-alarmrule-form" onSubmit={handleSubmit} noValidate>

        {/* Zeile 1: Name | Schweregrad */}
        <div className="new-alarmrule-grid-row">

          <div className="new-alarmrule-section">
            <SectionHeader
              name="name"
              number="❶"
              title="Name des Alarms"
              required
              tooltip="Vergib einen aussagekräftigen Namen, unter dem der Alarm in der Alarmübersicht angezeigt wird."
            />
            <div className="new-alarmrule-input-wrapper">
              <label className="new-alarmrule-floating-label" htmlFor="alarm-name">Name</label>
              <input
                type="text"
                id="alarm-name"
                className={`new-alarmrule-input${nameError ? ' new-alarmrule-input--invalid' : ''}`}
                value={name}
                onChange={e => setName(e.target.value)}
                onBlur={() => touch('name')}
                placeholder="z. B. Temperatur zu hoch"
                autoComplete="off"
                aria-invalid={nameError}
              />
            </div>
            {nameError && <FieldError>Dieses Feld ist erforderlich.</FieldError>}
          </div>

          <div className="new-alarmrule-section">
            <SectionHeader
              name="severity"
              number="❷"
              title="Schweregrad"
              required
              tooltip="Lege fest, wie kritisch der Alarm ist, wenn die Auslösebedingung erfüllt wird."
            />
            <SearchDropdown
              id="alarmrule-severity"
              label="Schweregrad"
              placeholder="Schweregrad wählen"
              inputValue={severityText}
              onInputChange={text => { setSeverityText(text); setSeverity(''); }}
              options={SEVERITY_OPTIONS}
              onSelect={o => { setSeverity(o.value); setSeverityText(o.label); }}
              onClear={() => { setSeverity(''); setSeverityText(''); }}
              invalid={severityError}
              onBlur={() => touch('severity')}
              focusNextOnSelect
            />
            {severityError && <FieldError>{selectionErrorText(severityText)}</FieldError>}
          </div>
        </div>

        {/* Zeile 2: Bezug | Endgerät/Gerätetyp */}
        <div className="new-alarmrule-grid-row">

          <div className="new-alarmrule-section">
            <SectionHeader
              name="mode"
              number="❸"
              title="Bezug"
              tooltip="Wähle, ob sich die Alarmregel auf ein einzelnes Endgerät oder auf alle Geräte eines bestimmten Gerätetyps beziehen soll."
            />
            <RadioGroup
              name="entity-mode"
              labelledBy="alarmrule-label-mode"
              options={ENTITY_MODE_OPTIONS}
              value={entityMode}
              onChange={setEntityMode}
            />
          </div>

          <div className="new-alarmrule-section">
            <SectionHeader
              name="entity"
              number="❹"
              title={`${entityLabel} auswählen`}
              required
              tooltip={entityMode === 'DEVICE'
                ? 'Wähle das Endgerät aus, für das die Alarmregel gelten soll.'
                : 'Wähle den Gerätetyp aus. Die Alarmregel gilt dann für alle Geräte dieses Typs.'}
            />
            <SearchDropdown
              id="alarmrule-entity"
              label={entityLabel}
              placeholder={`${entityLabel} wählen`}
              inputValue={entityText}
              onInputChange={text => { setEntityText(text); setSelectedEntity(''); }}
              options={entities.map(e => ({ value: e.id, label: e.name }))}
              onSelect={o => { setSelectedEntity(o.value); setEntityText(o.label); }}
              onClear={() => { setSelectedEntity(''); setEntityText(''); setSelectedKey(''); setKeyText(''); }}
              invalid={entityError}
              onBlur={() => touch('entity')}
              focusNextOnSelect
            />
            {entityError && <FieldError>{selectionErrorText(entityText)}</FieldError>}
          </div>
        </div>

        {/* Zeile 3: Datenkey */}
        <div className="new-alarmrule-grid-row">
          <div className={`new-alarmrule-section${!selectedEntity ? ' new-alarmrule-section--disabled' : ''}`}>
            <SectionHeader
              name="key"
              number="❺"
              title="Datenkey wählen"
              required
              tooltip="Wähle den Telemetrie-Schlüssel, dessen Wert überwacht werden soll."
            />
            <SearchDropdown
              id="alarmrule-key"
              label="Datenkey"
              placeholder="Datenkey wählen"
              inputValue={keyText}
              onInputChange={text => { setKeyText(text); setSelectedKey(''); }}
              options={dataKeys.map(k => ({ value: k, label: k }))}
              onSelect={o => { setSelectedKey(o.value); setKeyText(o.label); }}
              onClear={() => { setSelectedKey(''); setKeyText(''); }}
              disabled={!selectedEntity}
              invalid={keyError}
              onBlur={() => touch('key')}
              focusNextOnSelect
            />
            {keyError && <FieldError>{selectionErrorText(keyText)}</FieldError>}
          </div>
        </div>

        {/* Zeile 4: Auslösebedingung */}
        <div className="new-alarmrule-grid-row">
          <div className="new-alarmrule-section">
            <SectionHeader
              name="condition"
              number="❻"
              title="Auslösebedingung"
              tooltip="Lege fest, unter welcher Bedingung der Alarm ausgelöst wird."
            />
            <p className="new-alarmrule-subtitle">Alarm auslösen, wenn der Wert …</p>
            <RadioGroup
              name="condition"
              labelledBy="alarmrule-label-condition"
              options={CONDITION_OPTIONS}
              value={condition}
              onChange={changeCondition}
            />
          </div>
        </div>

        {/* Zeile 5: Zustand / Grenzwert(e) */}
        <div className="new-alarmrule-grid-row">
          <div className="new-alarmrule-section">
            <SectionHeader
              name="value"
              number="❼"
              title={valueSectionTitle}
              required
              tooltip="Lege den Wert fest, ab dem die gewählte Auslösebedingung erfüllt ist."
            />

            {isStateCondition && (
              <div className="new-alarmrule-input-wrapper">
                <label className="new-alarmrule-floating-label" htmlFor="state-value">Zustand</label>
                <input
                  type="text"
                  id="state-value"
                  className={`new-alarmrule-input${stateError ? ' new-alarmrule-input--invalid' : ''}`}
                  value={stateValue}
                  onChange={e => setStateValue(e.target.value)}
                  onBlur={() => touch('state')}
                  placeholder="z. B. true"
                  autoComplete="off"
                  aria-invalid={stateError}
                />
              </div>
            )}
            {isStateCondition && stateError && <FieldError>Dieses Feld ist erforderlich.</FieldError>}

            {isSingleThreshold && (
              <div className="new-alarmrule-input-wrapper">
                <label className="new-alarmrule-floating-label" htmlFor="threshold">Grenzwert</label>
                <input
                  type="number"
                  id="threshold"
                  className={`new-alarmrule-input${thresholdError ? ' new-alarmrule-input--invalid' : ''}`}
                  value={threshold}
                  onChange={e => setThreshold(e.target.value)}
                  onBlur={() => touch('threshold')}
                  placeholder="z. B. 20"
                  aria-invalid={thresholdError}
                />
              </div>
            )}
            {isSingleThreshold && thresholdError && <FieldError>Dieses Feld ist erforderlich.</FieldError>}

            {!isSingleThreshold && !isStateCondition && (
              <>
                <div className="new-alarmrule-threshold-grid">
                  <div>
                    <div className="new-alarmrule-input-wrapper">
                      <label className="new-alarmrule-floating-label" htmlFor="lower-threshold">Unterer Grenzwert</label>
                      <input
                        type="number"
                        id="lower-threshold"
                        className={`new-alarmrule-input${lowerError || rangeOrderError ? ' new-alarmrule-input--invalid' : ''}`}
                        value={lowerThreshold}
                        onChange={e => setLowerThreshold(e.target.value)}
                        onBlur={() => touch('lower')}
                        placeholder="z. B. 0"
                        aria-invalid={lowerError || rangeOrderError}
                      />
                    </div>
                    {lowerError && <FieldError>Dieses Feld ist erforderlich.</FieldError>}
                  </div>
                  <div>
                    <div className="new-alarmrule-input-wrapper">
                      <label className="new-alarmrule-floating-label" htmlFor="upper-threshold">Oberer Grenzwert</label>
                      <input
                        type="number"
                        id="upper-threshold"
                        className={`new-alarmrule-input${upperError || rangeOrderError ? ' new-alarmrule-input--invalid' : ''}`}
                        value={upperThreshold}
                        onChange={e => setUpperThreshold(e.target.value)}
                        onBlur={() => touch('upper')}
                        placeholder="z. B. 100"
                        aria-invalid={upperError || rangeOrderError}
                      />
                    </div>
                    {upperError && <FieldError>Dieses Feld ist erforderlich.</FieldError>}
                  </div>
                </div>
                {rangeOrderError && <FieldError>Der untere Grenzwert muss kleiner als der obere sein.</FieldError>}
              </>
            )}
          </div>
        </div>

        {/* Klartext-Zusammenfassung */}
        {selectedKey && entityText && (
          <div className="alarm-summary" role="status" aria-live="polite">
            {condition === 'ABOVE' && (
              <p>Alarm wird ausgelöst, wenn <strong>{selectedKey}</strong> von <strong>{entityText}</strong>{threshold.trim() !== '' ? <> den Wert <strong>{threshold}</strong> überschreitet (≥ {threshold})</> : <> einen Grenzwert überschreitet</>}.</p>
            )}
            {condition === 'BELOW' && (
              <p>Alarm wird ausgelöst, wenn <strong>{selectedKey}</strong> von <strong>{entityText}</strong>{threshold.trim() !== '' ? <> den Wert <strong>{threshold}</strong> unterschreitet (≤ {threshold})</> : <> einen Grenzwert unterschreitet</>}.</p>
            )}
            {condition === 'OUTSIDE' && (
              <p>Alarm wird ausgelöst, wenn <strong>{selectedKey}</strong> von <strong>{entityText}</strong> außerhalb des Bereichs <strong>{lowerThreshold || '?'}</strong> bis <strong>{upperThreshold || '?'}</strong> liegt (≤ {lowerThreshold || '?'} oder ≥ {upperThreshold || '?'}).</p>
            )}
            {condition === 'INSIDE' && (
              <p>Alarm wird ausgelöst, wenn <strong>{selectedKey}</strong> von <strong>{entityText}</strong> innerhalb des Bereichs <strong>{lowerThreshold || '?'}</strong> bis <strong>{upperThreshold || '?'}</strong> liegt (≥ {lowerThreshold || '?'} und ≤ {upperThreshold || '?'}).</p>
            )}            {condition === 'STATE' && (
              <p>Alarm wird ausgelöst, wenn <strong>{selectedKey}</strong> von <strong>{entityText}</strong> dem Zustand <strong>{stateValue.trim() !== '' ? stateValue : '?'}</strong> entspricht.</p>
            )}          </div>
        )}

        {!isValid && showErrors && (
          <p className="new-alarmrule-error-text" role="alert">
            Bitte fülle alle Pflichtfelder aus.
          </p>
        )}

        {submitError && (
          <p className="new-alarmrule-error-text" role="alert">{submitError}</p>
        )}

        <div className="new-alarmrule-required-explain" aria-hidden="true">
          <span className="new-alarmrule-required">*</span> Pflichtfeld
        </div>

        <div className="new-alarmrule-form-actions">
          <button type="submit" className="btn-primary">
            <Bestätigen /> Erstellen
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/alarm-rules')}>
            <Abbrechen /> Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
}

export default function NewAlarmruleWithBoundary() {
  return <ErrorBoundary><NewAlarmrule /></ErrorBoundary>;
}
